import { z } from 'zod'
import { userTypes, userPurposes } from '../constants/constants'
const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

// Unicode-aware name validation: letters in any language (é, ç, ã, ñ, ü, Cyrillic, …),
// combining accent marks, spaces, hyphens and apostrophes. No digits.
// `\p{L}`/`\p{M}` require the `u` flag.
export const nameRegex = /^[\p{L}\p{M} '-]+$/u
export const nameWithDigitsRegex = /^[\p{L}\p{M}0-9 '-]+$/u
export const nameWithPunctuationRegex = /^[\p{L}\p{M}0-9 '\-().,&]+$/u

const email = z
  .string()
  .transform((str) => str.trim()) // Trim spaces before validation
  .pipe(
    z
      .string()
      .min(1, 'Email is required')
      .max(254, 'Email is too long')
      .refine((email) => emailRegex.test(email), {
        message: 'Please enter a valid email address',
      }),
  )
  .refine((val) => val === val.trim(), {
    message: 'Email cannot start or end with spaces',
  })

const isAgree = z.boolean().refine((value) => value === true, { message: 'You must agree to the terms and conditions' })
const password = z
  .string()
  .trim()
  .min(12, 'Password must be at least 12 characters long')
  .regex(/^(?=.*[A-Z])/, 'Password must include at least one uppercase letter')
  .regex(/^(?=.*[a-z])/, 'Password must include at least one lowercase letter')
  .regex(/[\d\W_]/, 'Password must include a number or a symbol')
  .regex(/^(?!.*\s)/, 'Password cannot contain spaces')

// login must accept passwords created before the strength rules existed
const loginPassword = z.string().min(1, 'Password is required')

// signup shows a live requirements checklist, so the field itself stays quiet
const newPassword = z
  .string()
  .trim()
  .regex(/^(?=.*[A-Z])(?=.*[a-z])(?=.*[\d\W_])\S{12,}$/, 'Password does not meet the requirements below')

// single input holding first + last name; split on the first space when submitting
const fullName = z
  .string()
  .trim()
  .min(1, 'Name is required')
  .refine(
    (val) => {
      const spaceIndex = val.indexOf(' ')
      if (spaceIndex <= 0) return false
      return val.substring(spaceIndex + 1).trim().length > 0
    },
    { message: 'Please enter your first and last name' },
  )
  .refine((val) => val.split(/\s+/).every((part) => nameRegex.test(part)), {
    message: 'Name must contain only letters',
  })

const firstName = z
  .string()
  .trim()
  .min(1, 'First name is required')
  .max(50, 'First name cannot exceed 50 characters')
  .regex(nameRegex, 'First name must contain only letters')
  .refine((val) => val.length > 0, 'First name is required')

const lastName = z
  .string()
  .trim()
  .min(1, 'Last name is required')
  .max(50, 'Last name cannot exceed 50 characters')
  .regex(nameRegex, 'Last name must contain only letters')
  .refine((val) => val.length > 0, 'Last name is required')

const companyName = z
  .string()
  .trim()
  .min(1, 'Company name must be at least 2 characters long')
  .max(45, 'Company name cannot exceed 100 characters')
  .refine((value) => !/[<>]/.test(value), 'Company name must not contain "<" or ">" characters to prevent XSS attacks')

const projectName = z
  .string()
  .trim()
  .min(3, { message: 'Project name must be at least 3 characters' })
  .max(100, { message: 'Project name cannot exceed 100 characters' })

const userTypesArray = userTypes.map((type) => type.value)
const purposesArray = userPurposes.map((purpose) => purpose.value)

const description = z.enum(userTypesArray as [string, ...string[]])
const purpose = z.enum(purposesArray as [string, ...string[]])

export {
  isAgree,
  email,
  password,
  loginPassword,
  newPassword,
  fullName,
  firstName,
  lastName,
  companyName,
  projectName,
  description,
  purpose,
}
